import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { createUser } from "../Redux/Action";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";
import { notifyError, getLocalStorageItem } from "../Utils/Helper";
import { FiUpload, FiPlus, FiMinus, FiEye, FiEyeOff, FiMapPin } from "react-icons/fi";
import { detectCountryCode, COUNTRY_OPTIONS, ISO_TO_DIAL_CODE, getCountryName } from "../Utils/detectCountryCode";
import { REG_PARAMS, COMPANY_NAME } from "../Utils/Constants";

const STATIC_COUNTRY_CODES = ["+1", "+44", "+91", "+86", "+81", "+49", "+33", "+39", "+7", "+55", "+34", "+61", "+82", "+90", "+234", "+27", "+966", "+971", "+973", "+965", "+968", "+974", "+962", "+961", "+963", "+964", "+967", "+98", "+92", "+880", "+60", "+65", "+66", "+62", "+63", "+84", "+852", "+853", "+972", "+970", "+20", "+960", "+95", "+673", "+855", "+670", "+675", "+679", "+686", "+689", "+691", "+692", "+676", "+677", "+680", "+685", "+688", "+687", "+682", "+674", "+683", "+681"];

// White labels (by /getwhitelabel `user_name`) whose users get a trading-platform
// username. Keep in sync with USERNAME_WHITELABELS in
// metawallet-admin-backend/controllers/userController.js.
const USERNAME_WHITELABELS = ["GlobalApex"];

// Same rule as the user app's signup form and the backend
const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

// KYC document types: each decides which upload fields are shown.
// category is what the trading provider receives (identity / address / other).
const KYC_DOCUMENT_TYPES = [
  { value: "PASSPORT", label: "Passport", category: "identity", sides: [{ key: "front", label: "Photo page", required: true }] },
  {
    value: "NATIONAL_ID", label: "National ID card", category: "identity",
    sides: [{ key: "front", label: "Front side", required: true }, { key: "back", label: "Back side", required: false }],
  },
  {
    value: "DRIVING_LICENSE", label: "Driving licence", category: "identity",
    sides: [{ key: "front", label: "Front side", required: true }, { key: "back", label: "Back side", required: false }],
  },
  {
    value: "RESIDENCE_PERMIT", label: "Residence permit", category: "identity",
    sides: [{ key: "front", label: "Front side", required: true }, { key: "back", label: "Back side", required: false }],
  },
  { value: "PROOF_OF_ADDRESS", label: "Proof of address (utility bill / bank statement)", category: "address", sides: [{ key: "front", label: "Document", required: true }] },
  { value: "OTHER", label: "Other document", category: "other", sides: [{ key: "front", label: "Document", required: true }] },
];
const getDocumentType = (value) => KYC_DOCUMENT_TYPES.find((t) => t.value === value);
const MAX_KYC_DOCUMENTS = 10;
const ACCEPTED_FILE_TYPES = "image/*,application/pdf";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // matches the backend multer limit

let documentRowId = 0;
const newDocumentRow = (type = "") => ({ id: ++documentRowId, type, files: {} });

const EMPTY_FORM = {
  fname: "",
  lname: "",
  email: "",
  username: "",
  password: "",
  countryIso: "",
  countryCode: "+1", // Default country code
  mobile: "",
};

const AddUser = () => {
  // Use dynamic title management
  setDynamicTitle("Add User");

  const dispatch = useDispatch();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const showUsername = USERNAME_WHITELABELS.includes(getLocalStorageItem("whitelabelData")?.user_name);

  const [showPassword, setShowPassword] = useState(false);

  // KYC documents (at least one identity document is mandatory)
  const [kycDocuments, setKycDocuments] = useState(() => [newDocumentRow("PASSPORT")]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Country + code pre-filled from the admin's location; never overrides a manual pick
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isDetectingCountry, setIsDetectingCountry] = useState(true);
  const countryCodeTouched = useRef(false);
  const countryTouched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    detectCountryCode()
      .then((result) => {
        if (cancelled || !result) return;
        setDetectedCountry(result);
        setFormData((prev) => ({
          ...prev,
          countryCode: countryCodeTouched.current ? prev.countryCode : result.dialCode,
          countryIso: countryTouched.current ? prev.countryIso : result.iso,
        }));
      })
      .finally(() => {
        if (!cancelled) setIsDetectingCountry(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Picking a country also sets its dialling code, unless the code was chosen by hand
  const handleCountryChange = (e) => {
    const iso = e.target.value;
    countryTouched.current = true;
    setFormData((prev) => ({
      ...prev,
      countryIso: iso,
      countryCode: !countryCodeTouched.current && ISO_TO_DIAL_CODE[iso] ? ISO_TO_DIAL_CODE[iso] : prev.countryCode,
    }));
  };

  const updateDocument = (id, changes) =>
    setKycDocuments((prev) => prev.map((doc) => (doc.id === id ? { ...doc, ...changes } : doc)));

  // Changing the type resets the files, since the upload fields change
  const handleDocumentTypeChange = (id, type) => updateDocument(id, { type, files: {} });

  const handleDocumentFileChange = (doc, sideKey, e) => {
    const file = e.target.files?.[0] || null;
    if (file && file.size > MAX_FILE_SIZE) {
      notifyError(`${file.name} is larger than 10 MB.`);
      e.target.value = "";
      return;
    }
    updateDocument(doc.id, { files: { ...doc.files, [sideKey]: file } });
  };

  const addDocumentRow = () => {
    if (kycDocuments.length >= MAX_KYC_DOCUMENTS) return;
    setKycDocuments((prev) => [...prev, newDocumentRow()]);
  };

  const removeDocumentRow = (id) => setKycDocuments((prev) => prev.filter((doc) => doc.id !== id));

  // Returns an error message, or null when the KYC section is complete
  const validateKycDocuments = () => {
    const used = kycDocuments.filter((doc) => doc.type || Object.values(doc.files).some(Boolean));
    if (!used.length) return "KYC documents are required.";
    for (const [index, doc] of used.entries()) {
      const docType = getDocumentType(doc.type);
      if (!docType) return `Select a document type for document ${index + 1}.`;
      const missing = docType.sides.filter((side) => side.required && !doc.files[side.key]);
      if (missing.length) return `Upload the ${missing.map((s) => s.label.toLowerCase()).join(" and ")} for ${docType.label}.`;
    }
    if (!used.some((doc) => getDocumentType(doc.type)?.category === "identity")) {
      return "At least one identity document (passport, ID card, driving licence or residence permit) is required.";
    }
    return null;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation — every field the trading-provider registration needs
    if (
      !formData.fname || !formData.lname || !formData.email || (showUsername && !formData.username) ||
      !formData.password || !formData.mobile || !formData.countryCode || !formData.countryIso
    ) {
      notifyError("Please fill in all required fields");
      return;
    }
    if (showUsername && !USERNAME_REGEX.test(formData.username.trim())) {
      notifyError("Username must be 3-20 characters: letters, numbers or underscore.");
      return;
    }
    const kycError = validateKycDocuments();
    if (kycError) {
      notifyError(kycError);
      return;
    }

    setIsSubmitting(true);

    // Prepare form data for submission
    const submitData = new FormData();
    submitData.append("fname", formData.fname);
    submitData.append("lname", formData.lname);
    submitData.append("email", formData.email);
    if (showUsername) submitData.append("username", formData.username.trim());
    submitData.append("password", formData.password);
    submitData.append("country", getCountryName(formData.countryIso));
    submitData.append("countryCode", formData.countryCode);
    submitData.append("mobile", formData.mobile);
    // Same provider selection the Manage KYC approval sends
    if (REG_PARAMS) submitData.append("paramtype", REG_PARAMS);
    if (COMPANY_NAME) submitData.append("companyName", COMPANY_NAME);

    // Add whitelabelId if available
    const whitelabelData = getLocalStorageItem("whitelabelData");
    if (whitelabelData && whitelabelData._id) {
      submitData.append("whitelabelId", whitelabelData._id);
    }

    // KYC documents: files go in "documents", described in the same order by "documentsMeta"
    const documentsMeta = [];
    kycDocuments.forEach((doc) => {
      const docType = getDocumentType(doc.type);
      if (!docType) return;
      docType.sides.forEach((side) => {
        const file = doc.files[side.key];
        if (!file) return;
        submitData.append("documents", file);
        documentsMeta.push({ docType: docType.value, category: docType.category, side: side.key });
      });
    });
    submitData.append("documentsMeta", JSON.stringify(documentsMeta));

    // Success / error toasts come from the saga (notifyPromise)
    dispatch(
      createUser({
        payload: submitData,
        callback: (data) => {
          setIsSubmitting(false);
          if (data?.meta?.code === 200) {
            // Reset form
            countryCodeTouched.current = false;
            countryTouched.current = false;
            setFormData({
              ...EMPTY_FORM,
              countryCode: detectedCountry?.dialCode || "+1",
              countryIso: detectedCountry?.iso || "",
            });
            setKycDocuments([newDocumentRow("PASSPORT")]);
          }
        }
      })
    );
  };

  return (
    <div style={{ padding: '24px', width: '100%' }}>
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '8px', 
        padding: '24px', 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '800px',
        margin: '0 auto'
      }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
          Add New User
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                First Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="fname"
                value={formData.fname}
                onChange={handleInputChange}
                placeholder="Enter first name"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Last Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="lname"
                value={formData.lname}
                onChange={handleInputChange}
                placeholder="Enter last name"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Email <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter email address"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {showUsername && (
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Username <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="e.g. john_doe"
                maxLength={20}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${formData.username && !USERNAME_REGEX.test(formData.username) ? '#F04438' : '#E2E8F0'}`,
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <div style={{ marginTop: '4px', fontSize: '12px', color: formData.username && !USERNAME_REGEX.test(formData.username) ? '#D92D20' : '#667085' }}>
                3-20 characters: letters, numbers or underscore. Used to log in to the trading platform.
              </div>
            </div>
            )}
          </div>

          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Password <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter password"
              style={{
                width: 'calc(100% - 40px)',
                padding: '8px 12px',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              style={{
                position: 'absolute',
                right: '12px',
                top: '32px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#718096'
              }}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Country <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              name="countryIso"
              value={formData.countryIso}
              onChange={handleCountryChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="" disabled>
                {isDetectingCountry ? "Detecting your country…" : "Select country"}
              </option>
              {COUNTRY_OPTIONS.map((option) => (
                <option key={option.iso} value={option.iso}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Country Code <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={(e) => {
                  countryCodeTouched.current = true;
                  setFormData({...formData, countryCode: e.target.value});
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                {detectedCountry && !STATIC_COUNTRY_CODES.includes(detectedCountry.dialCode) && (
                  <option value={detectedCountry.dialCode}>
                    {detectedCountry.dialCode} ({detectedCountry.countryName})
                  </option>
                )}
                <option value="+1">+1 (US)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+91">+91 (India)</option>
                <option value="+86">+86 (China)</option>
                <option value="+81">+81 (Japan)</option>
                <option value="+49">+49 (Germany)</option>
                <option value="+33">+33 (France)</option>
                <option value="+39">+39 (Italy)</option>
                <option value="+7">+7 (Russia)</option>
                <option value="+55">+55 (Brazil)</option>
                <option value="+34">+34 (Spain)</option>
                <option value="+61">+61 (Australia)</option>
                <option value="+82">+82 (South Korea)</option>
                <option value="+90">+90 (Turkey)</option>
                <option value="+234">+234 (Nigeria)</option>
                <option value="+27">+27 (South Africa)</option>
                <option value="+966">+966 (Saudi Arabia)</option>
                <option value="+971">+971 (UAE)</option>
                <option value="+973">+973 (Bahrain)</option>
                <option value="+965">+965 (Kuwait)</option>
                <option value="+968">+968 (Oman)</option>
                <option value="+974">+974 (Qatar)</option>
                <option value="+962">+962 (Jordan)</option>
                <option value="+961">+961 (Lebanon)</option>
                <option value="+963">+963 (Syria)</option>
                <option value="+964">+964 (Iraq)</option>
                <option value="+967">+967 (Yemen)</option>
                <option value="+98">+98 (Iran)</option>
                <option value="+92">+92 (Pakistan)</option>
                <option value="+880">+880 (Bangladesh)</option>
                <option value="+60">+60 (Malaysia)</option>
                <option value="+65">+65 (Singapore)</option>
                <option value="+66">+66 (Thailand)</option>
                <option value="+62">+62 (Indonesia)</option>
                <option value="+63">+63 (Philippines)</option>
                <option value="+84">+84 (Vietnam)</option>
                <option value="+852">+852 (Hong Kong)</option>
                <option value="+853">+853 (Macau)</option>
                <option value="+972">+972 (Israel)</option>
                <option value="+970">+970 (Palestine)</option>
                <option value="+20">+20 (Egypt)</option>
                <option value="+960">+960 (Maldives)</option>
                <option value="+95">+95 (Myanmar)</option>
                <option value="+91">+91 (India)</option>
                <option value="+673">+673 (Brunei)</option>
                <option value="+855">+855 (Cambodia)</option>
                <option value="+670">+670 (East Timor)</option>
                <option value="+675">+675 (Papua New Guinea)</option>
                <option value="+679">+679 (Fiji)</option>
                <option value="+686">+686 (Kiribati)</option>
                <option value="+689">+689 (French Polynesia)</option>
                <option value="+691">+691 (Micronesia)</option>
                <option value="+692">+692 (Marshall Islands)</option>
                <option value="+676">+676 (Tonga)</option>
                <option value="+677">+677 (Solomon Islands)</option>
                <option value="+680">+680 (Palau)</option>
                <option value="+685">+685 (Samoa)</option>
                <option value="+688">+688 (Tuvalu)</option>
                <option value="+687">+687 (New Caledonia)</option>
                <option value="+682">+682 (Cook Islands)</option>
                <option value="+674">+674 (Nauru)</option>
                <option value="+683">+683 (Niue)</option>
                <option value="+681">+681 (Wallis and Futuna)</option>
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '12px', color: '#667085', minHeight: '16px' }}>
                {isDetectingCountry ? (
                  <span>Detecting your location…</span>
                ) : detectedCountry ? (
                  <>
                    <FiMapPin size={12} />
                    <span>
                      {detectedCountry.source === "timezone" ? "Based on your time zone" : "Detected from your location"}: {detectedCountry.countryName}
                      {formData.countryCode !== detectedCountry.dialCode && (
                        <button
                          type="button"
                          onClick={() => {
                            countryCodeTouched.current = false;
                            setFormData((prev) => ({ ...prev, countryCode: detectedCountry.dialCode }));
                          }}
                          style={{ marginLeft: '6px', background: 'none', border: 'none', padding: 0, color: '#2549ea', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Use {detectedCountry.dialCode}
                        </button>
                      )}
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Mobile Number <span style={{ color: 'red' }}>*</span>
              </label>
              <div style={{ display: 'flex' }}>
                <div style={{ 
                  padding: '8px 12px', 
                  border: '1px solid #E2E8F0', 
                  borderRight: 'none', 
                  borderRadius: '6px 0 0 6px',
                  backgroundColor: '#F7FAFC',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px'
                }}>
                  {formData.countryCode}
                </div>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Enter mobile number"
                  style={{ 
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #E2E8F0',
                    borderRadius: '0 6px 6px 0',
                    borderLeft: 'none',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <label style={{ display: 'block', fontWeight: '500' }}>
                KYC Documents <span style={{ color: 'red' }}>*</span>
              </label>
              <span style={{ fontSize: '12px', color: '#667085' }}>
                {kycDocuments.length}/{MAX_KYC_DOCUMENTS}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#667085', marginBottom: '12px' }}>
              At least one identity document is required. Images or PDF, up to 10 MB each.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {kycDocuments.map((doc, index) => {
                const docType = getDocumentType(doc.type);
                return (
                  <div
                    key={doc.id}
                    style={{
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      padding: '12px',
                      backgroundColor: '#FAFBFC'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '13px', color: '#344054' }}>
                          Document {index + 1} type {index === 0 && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <select
                          value={doc.type}
                          onChange={(e) => handleDocumentTypeChange(doc.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #E2E8F0',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value="" disabled>Select document type</option>
                          {KYC_DOCUMENT_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => removeDocumentRow(doc.id)}
                          aria-label={`Remove document ${index + 1}`}
                          title="Remove document"
                          style={{
                            height: '37px',
                            width: '37px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #FECDCA',
                            borderRadius: '6px',
                            backgroundColor: '#FEF3F2',
                            color: '#D92D20',
                            cursor: 'pointer'
                          }}
                        >
                          <FiMinus />
                        </button>
                      )}
                    </div>

                    {docType && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                        {docType.sides.map((side) => {
                          const file = doc.files[side.key];
                          const inputId = `kyc-${doc.id}-${side.key}`;
                          return (
                            <div key={side.key} style={{ flex: '1 1 220px', minWidth: 0 }}>
                              <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#344054' }}>
                                {side.label}{' '}
                                {side.required
                                  ? <span style={{ color: 'red' }}>*</span>
                                  : <span style={{ color: '#98A2B3', fontWeight: '400' }}>(optional)</span>}
                              </div>
                              <label
                                htmlFor={inputId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '10px 12px',
                                  border: `1px dashed ${file ? '#12B76A' : '#D0D5DD'}`,
                                  borderRadius: '6px',
                                  backgroundColor: file ? '#ECFDF3' : 'white',
                                  color: file ? '#027A48' : '#667085',
                                  fontSize: '13px',
                                  cursor: 'pointer',
                                  overflow: 'hidden'
                                }}
                              >
                                <FiUpload style={{ flexShrink: 0 }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {file ? file.name : 'Choose file'}
                                </span>
                              </label>
                              <input
                                id={inputId}
                                type="file"
                                accept={ACCEPTED_FILE_TYPES}
                                onChange={(e) => handleDocumentFileChange(doc, side.key, e)}
                                style={{ display: 'none' }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addDocumentRow}
              disabled={kycDocuments.length >= MAX_KYC_DOCUMENTS}
              style={{
                marginTop: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                border: '1px solid #D0D5DD',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#344054',
                fontSize: '14px',
                fontWeight: '500',
                cursor: kycDocuments.length >= MAX_KYC_DOCUMENTS ? 'not-allowed' : 'pointer',
                opacity: kycDocuments.length >= MAX_KYC_DOCUMENTS ? 0.5 : 1
              }}
            >
              <FiPlus /> Add another document
            </button>
          </div>

          <div style={{ height: '1px', backgroundColor: '#E2E8F0', margin: '16px 0' }}></div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{
                padding: '8px 24px',
                backgroundColor: '#3182CE',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              {isSubmitting ? (
                <>
                  <span>Creating User...</span>
                </>
              ) : (
                <>
                  <FiUpload />
                  <span>Create User</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { AddUser };